/**
 * Asset manifest — preloads all assets required for offline gameplay.
 *
 * Same-origin assets (bundled gameplay images + the per-stage result-screen
 * chip icons) are fetched into blob Object URLs, so they never depend on the
 * browser's HTTP cache surviving a connectivity drop (the dev server in
 * particular sends `Cache-Control: no-cache`, which forces a revalidation
 * round-trip that fails hard when offline — Vite doesn't truly inline these,
 * it just resolves an import to a URL the browser still has to fetch once).
 * CDN (CloudFront) overlays can't use this trick — they're cross-origin with
 * no CORS headers — so they're warmed into the HTTP cache via <img> loads
 * instead, a best-effort that relies on the CDN's own cache-control being
 * long-lived.
 */

import { IMAGES } from '@/assets/images';
import { getCloudFrontAssetUrl } from '@/utils/asset-utils';
import { appConfig } from '@app/config/appConfig';
import stage1RunningIcon from '@micro-screens/assets/icons/stage-1/running.png';
import stage1StarIcon from '@micro-screens/assets/icons/stage-1/star.png';
import stage2RunningIcon from '@micro-screens/assets/icons/stage-2/running.png';
import stage2StarIcon from '@micro-screens/assets/icons/stage-2/star.png';
import stage3RunningIcon from '@micro-screens/assets/icons/stage-3/running.png';
import stage3StarIcon from '@micro-screens/assets/icons/stage-3/star.png';
import stage4RunningIcon from '@micro-screens/assets/icons/stage-4/running.png';
import stage4StarIcon from '@micro-screens/assets/icons/stage-4/star.png';
import stage5RunningIcon from '@micro-screens/assets/icons/stage-5/running.png';
import stage5StarIcon from '@micro-screens/assets/icons/stage-5/star.png';
import stage6RunningIcon from '@micro-screens/assets/icons/stage-6/running.png';
import stage6StarIcon from '@micro-screens/assets/icons/stage-6/star.png';
import stage7RunningIcon from '@micro-screens/assets/icons/stage-7/running.png';
import stage7StarIcon from '@micro-screens/assets/icons/stage-7/star.png';
import { INSTRUCTION_ICONS } from '@micro-screens/src/section/instructions/instruction-icons';

// ── Same-origin assets served from the app origin ────────────────────────
// Every bundled gameplay image, plus the current stage's two result-screen
// chip icons (only the current stage matters — this game runs a single
// fixed stage, VITE_STAGE_NUMBER).

const RUNNING_ICON_BY_STAGE: Partial<Record<number, string>> = {
  1: stage1RunningIcon,
  2: stage2RunningIcon,
  3: stage3RunningIcon,
  4: stage4RunningIcon,
  5: stage5RunningIcon,
  6: stage6RunningIcon,
  7: stage7RunningIcon,
};

const STAR_ICON_BY_STAGE: Partial<Record<number, string>> = {
  1: stage1StarIcon,
  2: stage2StarIcon,
  3: stage3StarIcon,
  4: stage4StarIcon,
  5: stage5StarIcon,
  6: stage6StarIcon,
  7: stage7StarIcon,
};

const bootStage = appConfig.socket.stageNumber;

const PUBLIC_ASSET_URLS = [
  ...Object.values(IMAGES),
  ...Object.values(INSTRUCTION_ICONS),
  RUNNING_ICON_BY_STAGE[bootStage] ?? stage1RunningIcon,
  STAR_ICON_BY_STAGE[bootStage] ?? stage1StarIcon,
];

// ── CDN overlay images used by the instructions + result screens ────────

const SECONDARY_OVERLAY_FILE_BY_STAGE: Partial<Record<number, string>> = {
  1: 'end_overlay.png',
  2: 'end_overlay.png',
  3: 'end_overlay.png',
  4: 'end_overlay2.png',
  5: 'end_overlay5.png',
  6: 'end_overlay6.png',
  7: 'end_overlay7.png',
};

const bootStageStr = String(bootStage);

const CDN_OVERLAY_URLS = [
  getCloudFrontAssetUrl(`stage-${bootStageStr}/Stage_${bootStageStr}.png`),
  getCloudFrontAssetUrl(`stage-${bootStageStr}/stage-${bootStageStr}-ending.png`),
  getCloudFrontAssetUrl(SECONDARY_OVERLAY_FILE_BY_STAGE[bootStage] ?? 'end_overlay.png'),
];

// ── Object URL cache ─────────────────────────────────────────────────────
// Gameplay code reads from this instead of re-requesting the original URL,
// so it never depends on the browser's HTTP cache surviving a connectivity drop.

const objectUrlCache = new Map<string, string>();

export function getPreloadedAssetUrl(originalUrl: string): string {
  return objectUrlCache.get(originalUrl) ?? originalUrl;
}

// ── Fetchers ─────────────────────────────────────────────────────────────

async function fetchAsset(url: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to preload asset: ${url} (${String(response.status)})`);
  }
  const blob = await response.blob();
  objectUrlCache.set(url, URL.createObjectURL(blob));
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve();
    };
    img.onerror = () => {
      reject(new Error(`Failed to preload image: ${url}`));
    };
    img.src = url;
  });
}

// ── Main preloader ───────────────────────────────────────────────────────

export async function preloadAllAssets(): Promise<void> {
  await Promise.all([
    ...PUBLIC_ASSET_URLS.map(fetchAsset),
    // CDN overlays are decorative extras (instructions/result-screen
    // backgrounds) warmed on a best-effort basis — a CDN outage or an
    // unconfigured VITE_CLOUDFRONT_URL (dev/local) must not block the whole
    // game from becoming playable over it.
    ...CDN_OVERLAY_URLS.map((url) =>
      preloadImage(url).catch((err: unknown) => {
        console.warn('[assetManifest] CDN overlay preload failed (non-fatal):', err);
      }),
    ),
  ]);
}
